import { useTranslation } from 'react-i18next'
import { QueBoardSession, type QuetureTestQueBoardConfig } from '@quebon/shared-learning'
import type { ComponentProgressDetailView, QueboardConfigView } from '@/types/coaching'
import { PartialDataFallback } from './parts/PartialDataFallback'

interface Props {
  component: ComponentProgressDetailView
}

function toLearnerQueBoardConfig(config: QueboardConfigView): QuetureTestQueBoardConfig {
  const modules = Array.isArray(config.tutorial.modules)
    ? config.tutorial.modules.filter((module): module is string => typeof module === 'string')
    : []

  return {
    problem: config.problem,
    features: {
      tie: config.features.tie === true,
      teleport5: config.features.teleport5 === true,
      teleport10: config.features.teleport10 === true,
      bank: config.features.bank === true,
      indicator: config.features.indicator === true,
      customInput: config.features.customInput === true,
    },
    tutorial: {
      enabled: config.tutorial.enabled === true,
      modules,
      autoStart: config.tutorial.autoStart === true,
    },
    character: config.character,
  }
}

export function InteractiveCoachingDetail({ component }: Props) {
  const { t } = useTranslation('coaching')
  const it = component.interactive
  if (!it) return <PartialDataFallback type="interactive" />
  const configs = it.configs?.map(toLearnerQueBoardConfig) ?? []

  if (it.kind === 'game') {
    return <p className="text-xs italic text-gray-500">{t('placeholder.gameNotImplemented')}</p>
  }

  if (configs.length === 0) {
    return <p className="text-xs italic text-gray-500">{t('placeholder.noConfigs')}</p>
  }

  return (
    <section
      aria-label={t('component.type.interactive')}
      className="mx-auto w-full overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200"
    >
      <QueBoardSession
        key={component.componentProgressId}
        variant="inline"
        isOpen
        configs={configs}
      />
    </section>
  )
}
